/**
 * StrategicTimelineView - Displays 3/6/9/12/24 month strategic plans
 * Reads from sentimentReport.analysis.strategic_timeline (dept level)
 * or orgReport.analysis.org_strategic_timeline (org level)
 */
import React from 'react';
import {
    Calendar, Target, CheckCircle2, Clock, TrendingUp,
    Zap, Star, ArrowRight, Loader2, BarChart3, RefreshCw
} from 'lucide-react';

const TIMELINE_PERIODS = [
    { key: '3_month', label: '3 Months', color: '#007AFF', icon: <Zap size={16} /> },
    { key: '6_month', label: '6 Months', color: '#5856D6', icon: <TrendingUp size={16} /> },
    { key: '9_month', label: '9 Months', color: '#FF9500', icon: <Target size={16} /> },
    { key: '12_month', label: '12 Months', color: '#34C759', icon: <Star size={16} /> },
    { key: '24_month', label: '24 Months', color: '#FF2D55', icon: <Calendar size={16} /> },
];

function StrategicTimelineView({ sentimentReport, orgReport, isOrgLevel = false, loading, onRegenerate }) {
    if (loading) {
        return (
            <div className="rp-loading">
                <Loader2 className="rp-spinner-icon" size={24} />
                <p>Loading strategic timeline...</p>
            </div>
        );
    }

    const timeline = isOrgLevel
        ? orgReport?.analysis?.org_strategic_timeline
        : sentimentReport?.analysis?.strategic_timeline;

    // Check if report exists but is missing new fields (stale data)
    const hasReport = isOrgLevel ? !!orgReport?.analysis : !!sentimentReport?.analysis;

    if (!timeline) {
        return (
            <div className="rp-empty-state">
                <div className="rp-empty-icon"><Calendar size={32} /></div>
                <h3>{hasReport ? 'Report Needs Regeneration' : 'No Strategic Timeline Available'}</h3>
                <p>{hasReport 
                    ? 'This report was generated before strategic timeline analysis was available. Regenerate to unlock this feature.'
                    : isOrgLevel 
                        ? 'Generate an organization report first to see cross-department timelines.'
                        : 'Generate a department sentiment report first to see strategic timelines.'}
                </p>
                {onRegenerate && (
                    <button className="rp-btn rp-btn-primary" onClick={onRegenerate} style={{ marginTop: '1rem' }}>
                        <RefreshCw size={14} /> {hasReport ? 'Regenerate Report' : 'Generate Report'}
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="strategic-timeline-view">
            <div className="ds-section">
                <div className="ds-section-header">
                    <h2><Calendar size={18} style={{ marginRight: 8 }} /> Strategic Roadmap</h2>
                    <p className="ds-section-subtitle">
                        {isOrgLevel 
                            ? 'Organization-wide implementation plan with department-specific milestones'
                            : 'Phased improvement plan based on survey data and AI analysis'}
                    </p>
                </div>
                <div className="ds-section-body">
                    <div className="timeline-roadmap">
                        {TIMELINE_PERIODS.map((period, idx) => {
                            const data = timeline[period.key];
                            if (!data) return null;

                            const theme = isOrgLevel ? data.org_theme : data.theme;
                            const goals = isOrgLevel ? data.transformation_goals || data.cross_cutting_initiatives : data.goals;
                            const milestones = data.milestones || data.quick_wins || [];
                            const metrics = data.success_metrics;

                            return (
                                <div key={period.key} className="timeline-phase" style={{ borderLeftColor: period.color }}>
                                    <div className="timeline-phase-header">
                                        <div className="timeline-phase-badge" style={{ backgroundColor: period.color }}>
                                            {period.icon}
                                            <span>{period.label}</span>
                                        </div>
                                        {theme && <h3 className="timeline-phase-theme">{theme}</h3>}
                                    </div>

                                    {goals && goals.length > 0 && (
                                        <div className="timeline-goals">
                                            <h4><Target size={14} /> Goals</h4>
                                            <ul>
                                                {goals.map((goal, i) => (
                                                    <li key={i}><CheckCircle2 size={12} /> {goal}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {milestones.length > 0 && (
                                        <div className="timeline-milestones">
                                            <h4><Star size={14} /> {data.quick_wins ? 'Quick Wins' : 'Milestones'}</h4>
                                            <ul>
                                                {milestones.map((m, i) => (
                                                    <li key={i}><ArrowRight size={12} /> {m}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Org-level: department priorities/milestones */}
                                    {isOrgLevel && data.department_priorities && (
                                        <div className="timeline-dept-priorities">
                                            <h4><BarChart3 size={14} /> Department Priorities</h4>
                                            {data.department_priorities.map((dp, i) => (
                                                <div key={i} className="timeline-dept-item">
                                                    <strong>{dp.department}:</strong> {dp.focus}
                                                    {dp.quick_wins && dp.quick_wins.length > 0 && (
                                                        <ul>{dp.quick_wins.map((qw, j) => <li key={j}>{qw}</li>)}</ul>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {isOrgLevel && data.department_milestones && (
                                        <div className="timeline-dept-priorities">
                                            <h4><BarChart3 size={14} /> Department Milestones</h4>
                                            {data.department_milestones.map((dm, i) => (
                                                <div key={i} className="timeline-dept-item">
                                                    <strong>{dm.department}:</strong> {dm.milestone || dm.target}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {data.resources_needed && (
                                        <div className="timeline-resources">
                                            <h4><Clock size={14} /> Resources Needed</h4>
                                            <p>{data.resources_needed}</p>
                                        </div>
                                    )}

                                    {metrics && (
                                        <div className="timeline-metrics">
                                            <h4><TrendingUp size={14} /> Success Metrics</h4>
                                            <p>{metrics}</p>
                                        </div>
                                    )}

                                    {data.vision && (
                                        <div className="timeline-vision">
                                            <h4><Star size={14} /> Vision</h4>
                                            <p>{data.vision}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StrategicTimelineView;
